import { useEffect, useRef, useState } from 'react';
import {
  getDocumentsStatus,
  getUpdateSummary,
  resetDocumentsStatus,
  triggerLotUpdateExtraction,
} from '../lib/lotExtractionService';
import { createLot, getLotsByChantier, purgeLot, updateLot, updateLotMetiers } from '../lib/lotService';
import { getAllMetiers } from '../lib/metierService';

const POLL_INTERVAL_MS = 3000;

const STEP_LABELS = ['Envoi', 'Analyse', 'Mise à jour'];

const FIELD_LABELS = {
  description: 'description',
  montant_previsionnel: 'montant',
  date_debut_prevue: 'date de début',
  duree_jours: 'durée',
  metiers: 'métiers',
  dates_taches: 'dates des tâches',
  contact: 'contact',
};

function buildChangesByLotId(summary) {
  const changes = new Map();
  for (const lot of summary?.created_lots ?? []) {
    changes.set(lot.lot_id, { origin: 'created', fields: [], tasksAdded: lot.tasks_added });
  }
  for (const lot of summary?.updated_lots ?? []) {
    changes.set(lot.lot_id, { origin: 'updated', fields: lot.fields, tasksAdded: lot.tasks_added });
  }
  return changes;
}

function changeSummary(change) {
  const parts = [];
  if (change.fields.length > 0) {
    parts.push(`Complété : ${change.fields.map((field) => FIELD_LABELS[field] ?? field).join(', ')}`);
  }
  if (change.tasksAdded > 0) {
    parts.push(`+${change.tasksAdded} tâche${change.tasksAdded > 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
}

async function fetchNextLotNumero(chantierId) {
  const lots = await getLotsByChantier(chantierId);
  return lots.reduce((max, lot) => Math.max(max, lot.numero), 0) + 1;
}

function initEditsFromLots(lots, changes) {
  const map = {};
  for (const lot of lots) {
    map[lot.id] = {
      lotId: lot.id,
      numero: lot.numero,
      nom: lot.nom,
      description: lot.description ?? '',
      montantPrevisionnel: lot.montant_previsionnel ?? null,
      dateDebutPrevue: lot.date_debut_prevue ?? null,
      dureeJours: lot.duree_jours ?? null,
      metiers: (lot.lot_metiers || []).map((lm) => lm.metier).filter(Boolean),
      fieldsDirty: false,
      metiersDirty: false,
      deleted: false,
      isNew: false,
      change: changes.get(lot.id),
    };
  }
  return map;
}

function lotSummary(edit) {
  const parts = [];
  if (edit.montantPrevisionnel != null) parts.push(`${edit.montantPrevisionnel.toLocaleString('fr-FR')} €`);
  if (edit.dureeJours != null) parts.push(`${edit.dureeJours} j`);
  if (edit.metiers.length > 0) parts.push(`${edit.metiers.length} métier${edit.metiers.length > 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(' · ') : 'Aucune information renseignée';
}

// Écran de suivi de l'extraction IA des lots + relecture/édition avant de
// rejoindre le chantier — équivalent web de lots-review.tsx côté mobile.
function LotsReviewStep({ chantierId, documentIds, onFinish }) {
  const [phase, setPhase] = useState('waiting');
  const [errorMessage, setErrorMessage] = useState(null);
  const [docsStatus, setDocsStatus] = useState([]);
  const [edits, setEdits] = useState({});
  const [expandedLotIds, setExpandedLotIds] = useState(new Set());
  const [metiers, setMetiers] = useState([]);
  const [newMetierSelection, setNewMetierSelection] = useState({});
  const [saving, setSaving] = useState(false);

  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const nextTempIdRef = useRef(-1);

  function clearTimers() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  }

  async function checkStatus() {
    try {
      const docs = await getDocumentsStatus(documentIds);
      if (!mountedRef.current) return;
      setDocsStatus(docs);

      const errored = docs.find((d) => d.lot_extraction_status === 'error');
      if (errored) {
        clearTimers();
        setErrorMessage(errored.extraction_error || "Erreur inconnue lors de l'analyse.");
        setPhase('error');
        return;
      }

      const allDone = docs.length > 0 && docs.every((d) => d.lot_extraction_status === 'done');
      if (allDone) {
        clearTimers();
        setPhase('finalizing');
      }
    } catch (e) {
      console.error('Erreur polling extraction lots:', e);
    }
  }

  function startPolling() {
    clearTimers();
    checkStatus();
    pollRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
  }

  useEffect(() => {
    mountedRef.current = true;
    startPolling();
    return () => {
      mountedRef.current = false;
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== 'finalizing') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [allLots, summary] = await Promise.all([
          getLotsByChantier(chantierId),
          getUpdateSummary(documentIds),
        ]);
        const changes = buildChangesByLotId(summary);
        const lots = allLots.filter((lot) => changes.has(lot.id));
        await new Promise((resolve) => setTimeout(resolve, 450));
        if (cancelled || !mountedRef.current) return;
        setEdits(initEditsFromLots(lots, changes));
        const firstLot = [...lots].sort((a, b) => a.numero - b.numero)[0];
        setExpandedLotIds(firstLot ? new Set([firstLot.id]) : new Set());
        setPhase('ready');
      } catch (e) {
        console.error('Erreur récupération des lots créés:', e);
        if (!cancelled && mountedRef.current) {
          setErrorMessage('Impossible de récupérer les lots créés.');
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, chantierId]);

  useEffect(() => {
    if (phase !== 'ready' || metiers.length > 0) return undefined;
    let cancelled = false;
    getAllMetiers()
      .then((data) => !cancelled && setMetiers(data))
      .catch((err) => console.error('Erreur chargement métiers:', err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  async function handleRetry() {
    setErrorMessage(null);
    setDocsStatus([]);
    setPhase('waiting');
    try {
      await resetDocumentsStatus(documentIds);
      await triggerLotUpdateExtraction(chantierId, documentIds);
      startPolling();
    } catch (e) {
      console.error('Erreur relance extraction lots:', e);
      setErrorMessage("Impossible de relancer l'analyse. Réessayez plus tard.");
      setPhase('error');
    }
  }

  function toggleLotExpanded(lotId) {
    setExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  }

  function updateField(lotId, key, value) {
    setEdits((prev) => ({ ...prev, [lotId]: { ...prev[lotId], [key]: value, fieldsDirty: true } }));
  }

  function addMetier(lotId) {
    const metierId = Number(newMetierSelection[lotId]);
    const metier = metiers.find((m) => m.id === metierId);
    if (!metier) return;
    setEdits((prev) => {
      const current = prev[lotId];
      if (current.metiers.some((m) => m.id === metier.id)) return prev;
      return { ...prev, [lotId]: { ...current, metiers: [...current.metiers, metier], metiersDirty: true } };
    });
    setNewMetierSelection((prev) => ({ ...prev, [lotId]: '' }));
  }

  function removeMetier(lotId, metierId) {
    setEdits((prev) => ({
      ...prev,
      [lotId]: {
        ...prev[lotId],
        metiers: prev[lotId].metiers.filter((m) => m.id !== metierId),
        metiersDirty: true,
      },
    }));
  }

  function confirmDeleteLot(lotId, nom) {
    if (!window.confirm(`« ${nom} » sera retiré du projet. Confirmer la suppression ?`)) return;
    setEdits((prev) => ({ ...prev, [lotId]: { ...prev[lotId], deleted: true } }));
  }

  function addLot() {
    const tempId = nextTempIdRef.current--;
    const visible = Object.values(edits).filter((e) => !e.deleted);
    setEdits((prev) => ({
      ...prev,
      [tempId]: {
        lotId: tempId,
        numero: visible.reduce((max, e) => Math.max(max, e.numero), 0) + 1,
        nom: '',
        description: '',
        montantPrevisionnel: null,
        dateDebutPrevue: null,
        dureeJours: null,
        metiers: [],
        fieldsDirty: true,
        metiersDirty: false,
        deleted: false,
        isNew: true,
      },
    }));
    setExpandedLotIds((prev) => new Set(prev).add(tempId));
  }

  async function handleValidate() {
    if (saving) return;
    setSaving(true);
    try {
      for (const edit of Object.values(edits)) {
        if (!edit.deleted) continue;
        if (!edit.isNew) await purgeLot(edit.lotId);
      }

      let nextNumero = await fetchNextLotNumero(chantierId);

      for (const edit of Object.values(edits)) {
        if (edit.deleted) continue;
        if (edit.isNew) {
          const created = await createLot(chantierId, {
            numero: nextNumero++,
            nom: edit.nom.trim() || 'Lot sans nom',
            description: edit.description.trim() || null,
            montant_previsionnel: edit.montantPrevisionnel,
            date_debut_prevue: edit.dateDebutPrevue,
            duree_jours: edit.dureeJours,
          });
          if (edit.metiers.length > 0) {
            await updateLotMetiers(created.id, edit.metiers.map((m) => m.id));
          }
          continue;
        }
        if (edit.fieldsDirty) {
          await updateLot(edit.lotId, {
            nom: edit.nom.trim(),
            description: edit.description.trim() || null,
            montant_previsionnel: edit.montantPrevisionnel,
            date_debut_prevue: edit.dateDebutPrevue,
            duree_jours: edit.dureeJours,
          });
        }
        if (edit.metiersDirty) {
          await updateLotMetiers(edit.lotId, edit.metiers.map((m) => m.id));
        }
      }
      onFinish();
    } catch (e) {
      console.error('Erreur validation lots:', e);
      window.alert("Impossible d'enregistrer les modifications. Réessayez.");
    } finally {
      setSaving(false);
    }
  }

  const visibleEdits = Object.values(edits)
    .filter((e) => !e.deleted)
    .sort((a, b) => a.numero - b.numero);
  const allExpanded = visibleEdits.length > 0 && visibleEdits.every((e) => expandedLotIds.has(e.lotId));

  function toggleAllExpanded() {
    setExpandedLotIds(allExpanded ? new Set() : new Set(visibleEdits.map((e) => e.lotId)));
  }

  const stepIndex =
    phase === 'finalizing' ? 2 : docsStatus.some((d) => d.lot_extraction_status === 'processing') ? 1 : 0;
  const fillClass = phase === 'finalizing' ? 'is-done' : stepIndex === 0 ? 'is-sending' : 'is-analyzing';

  return (
    <>
      <div className="Modal-header">
        <div>
          <h2 className="Modal-title">Mises à jour détectées</h2>
          <p className="Modal-subtitle">
            {phase === 'waiting' && 'Analyse en cours…'}
            {phase === 'finalizing' && 'Application des mises à jour…'}
            {phase === 'ready' && 'Vérifiez avant de continuer'}
            {phase === 'error' && 'Analyse indisponible'}
          </p>
        </div>
      </div>

      <div className="Modal-body">
        {(phase === 'waiting' || phase === 'finalizing') && (
          <div className="LotsReview-center">
            <span className="Spinner" />
            <p className="LotsReview-centerTitle">
              {stepIndex === 0
                ? "Envoi de la demande d'analyse…"
                : stepIndex === 1
                ? 'Analyse des documents en cours…'
                : 'Application des mises à jour…'}
            </p>
            <div className="LotsReview-steps">
              <div className="LotsReview-track">
                <div className={`LotsReview-fill ${fillClass}`} />
              </div>
              <div className="LotsReview-stepLabels">
                {STEP_LABELS.map((label, i) => (
                  <span key={label} className={i <= stepIndex ? 'is-reached' : ''}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <p className="LotsReview-centerSub">
              {stepIndex === 2
                ? 'Presque terminé — préparation de la liste des lots.'
                : "Cela peut prendre jusqu'à 3 minutes pour les documents volumineux. Les mises à jour détectées apparaîtront ici."}
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div className="LotsReview-center">
            <p className="LotsReview-centerTitle">L'analyse a échoué</p>
            <p className="LotsReview-centerSub">
              {errorMessage ?? "Une erreur est survenue pendant l'analyse des documents."}
            </p>
          </div>
        )}

        {phase === 'ready' && (
          <div className="Modal-form">
            {visibleEdits.length === 0 ? (
              <p className="LotsReview-centerSub">
                Aucune mise à jour détectée dans les documents fournis.
              </p>
            ) : (
              <>
                {visibleEdits.length > 1 && (
                  <div className="LotsReview-listHeader">
                    <span>
                      {visibleEdits.length} lots concernés
                    </span>
                    <button type="button" className="LotsReview-toggleAll" onClick={toggleAllExpanded}>
                      {allExpanded ? 'Tout replier' : 'Tout déplier'}
                    </button>
                  </div>
                )}

                {visibleEdits.map((edit) => {
                  const expanded = expandedLotIds.has(edit.lotId);
                  const availableMetiers = metiers.filter(
                    (m) => !edit.metiers.some((em) => em.id === m.id)
                  );
                  const canDelete = edit.change?.origin !== 'updated';
                  const changeText = edit.change ? changeSummary(edit.change) : '';
                  return (
                    <div className="LotCard" key={edit.lotId}>
                      <div className="LotCard-header">
                        <button
                          type="button"
                          className="LotCard-headerMain"
                          onClick={() => toggleLotExpanded(edit.lotId)}
                        >
                          <span className="LotCard-numero">{edit.numero}</span>
                          <span className="LotCard-headerText">
                            <span className="LotCard-titleRow">
                              <span className="LotCard-title">{edit.nom.trim() || 'Lot sans nom'}</span>
                              {edit.change && (
                                <span className={`LotCard-changeBadge is-${edit.change.origin}`}>
                                  {edit.change.origin === 'created' ? 'Nouveau' : 'Mis à jour'}
                                </span>
                              )}
                            </span>
                            {changeText && (
                              <span className={`LotCard-changeSummary is-${edit.change.origin}`}>{changeText}</span>
                            )}
                            {!expanded && <span className="LotCard-summary">{lotSummary(edit)}</span>}
                          </span>
                          <span className="LotCard-chevron">{expanded ? '▲' : '▼'}</span>
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="LotCard-delete"
                            onClick={() => confirmDeleteLot(edit.lotId, edit.nom)}
                            aria-label="Supprimer le lot"
                          >
                            🗑
                          </button>
                        )}
                      </div>

                      {expanded && (
                        <div className="LotCard-body">
                          <input
                            className="LotCard-nomInput"
                            value={edit.nom}
                            onChange={(e) => updateField(edit.lotId, 'nom', e.target.value)}
                            placeholder="Nom du lot"
                          />
                          <textarea
                            className="LotCard-descInput"
                            value={edit.description}
                            onChange={(e) => updateField(edit.lotId, 'description', e.target.value)}
                            placeholder="Description (optionnel)"
                          />

                          <div className="LotCard-row">
                            <label className="LotCard-fieldHalf">
                              <span className="LotCard-fieldLabel">Montant prévisionnel</span>
                              <input
                                className="LotCard-numInput"
                                type="number"
                                value={edit.montantPrevisionnel ?? ''}
                                onChange={(e) =>
                                  updateField(
                                    edit.lotId,
                                    'montantPrevisionnel',
                                    e.target.value === '' ? null : Number(e.target.value)
                                  )
                                }
                                placeholder="€"
                              />
                            </label>
                            <label className="LotCard-fieldHalf">
                              <span className="LotCard-fieldLabel">Durée (jours)</span>
                              <input
                                className="LotCard-numInput"
                                type="number"
                                value={edit.dureeJours ?? ''}
                                onChange={(e) =>
                                  updateField(
                                    edit.lotId,
                                    'dureeJours',
                                    e.target.value === '' ? null : parseInt(e.target.value, 10)
                                  )
                                }
                                placeholder="j"
                              />
                            </label>
                          </div>

                          <label className="LotCard-field">
                            <span className="LotCard-fieldLabel">Date de début prévue</span>
                            <input
                              className="LotCard-numInput"
                              type="date"
                              value={edit.dateDebutPrevue ?? ''}
                              onChange={(e) => updateField(edit.lotId, 'dateDebutPrevue', e.target.value || null)}
                            />
                          </label>

                          <div className="LotCard-metiersRow">
                            {edit.metiers.map((m) => (
                              <span
                                key={m.id}
                                className="LotCard-metierChip"
                                style={{ backgroundColor: `${m.color || '#f6a500'}25`, color: m.color || '#f6a500' }}
                              >
                                {m.name}
                                <button type="button" onClick={() => removeMetier(edit.lotId, m.id)}>×</button>
                              </span>
                            ))}
                            {availableMetiers.length > 0 && (
                              <span className="LotCard-addMetier">
                                <select
                                  value={newMetierSelection[edit.lotId] || ''}
                                  onChange={(e) =>
                                    setNewMetierSelection((prev) => ({ ...prev, [edit.lotId]: e.target.value }))
                                  }
                                >
                                  <option value="">+ métier</option>
                                  {availableMetiers.map((m) => (
                                    <option key={m.id} value={m.id}>
                                      {m.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  disabled={!newMetierSelection[edit.lotId]}
                                  onClick={() => addMetier(edit.lotId)}
                                >
                                  Ajouter
                                </button>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            <button type="button" className="LotsReview-addLot" onClick={addLot}>
              + Ajouter un lot
            </button>
          </div>
        )}
      </div>

      <div className="Modal-footer">
        {phase === 'error' && (
          <>
            <button className="Modal-btn Modal-btn-secondary" onClick={handleRetry}>
              Réessayer
            </button>
            <button className="Modal-btn Modal-btn-primary" onClick={onFinish}>
              Fermer
            </button>
          </>
        )}
        {phase === 'ready' && (
          <button className="Modal-btn Modal-btn-primary" onClick={handleValidate} disabled={saving}>
            {saving ? (
              <span className="Modal-btn-loading">
                <span className="Modal-btnSpinner" />
                Enregistrement…
              </span>
            ) : (
              'Valider les mises à jour'
            )}
          </button>
        )}
      </div>
    </>
  );
}

export default LotsReviewStep;
